import { useMemo, useCallback } from "react";
import {
  EntitySelector,
  EntitySelectorTrigger,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorEmpty,
  type EntitySelectorConfig,
  type EntitySelectorVariant,
  type EntitySelection,
} from "@salestrak/ui";
import { IssueTypePool } from "./issue-type-pool";
import { selectionToIssueTypeEntity } from "./utils";
import { SelectionEntityTrigger } from "./selection-entity-trigger";
import { useIssueTypePool, type IssueTypeInput } from "./hooks";
import { toEntityTriggerSize, type SelectorSize } from "./sizes";
import type { Omittable } from "@salestrak/ui";

export type IssueTypeSelectorProps = Omittable<{
  /** Available issue types */
  issueTypes: IssueTypeInput[];
  /** Currently selected issue type ID */
  value?: string | null;
  /** Called when an issue type is selected */
  onValueChange?: (issueTypeId: string) => void;
  /** "popover" (default) wraps in popover; "inline" renders content directly */
  variant?: EntitySelectorVariant;
  placeholder?: string;
  emptyTextVariant?: React.ComponentProps<typeof SelectionEntityTrigger>["emptyTextVariant"];
  size?: SelectorSize;
  disabled?: boolean;
  /** Render the selected Type as static information without opening a picker. */
  readOnly?: boolean;
  /** Render the selected Type with a muted label in the entity-value trigger. */
  valueMuted?: boolean;
  /** Trigger button treatment for the entity-value trigger. */
  buttonVariant?: React.ComponentProps<typeof SelectionEntityTrigger>["buttonVariant"];
  /** Show the disclosure chevron on the entity-value trigger. */
  showChevron?: boolean;
  /** Trigger corners — `"square"` when the control is a hero or row control. */
  shape?: React.ComponentProps<typeof SelectionEntityTrigger>["shape"];
  /** Tight hero packing — see `SelectionEntityTrigger.dense`. */
  dense?: boolean;
  children?: React.ReactNode;
}>;

export function IssueTypeSelector({
  issueTypes,
  value,
  onValueChange,
  variant,
  placeholder = "Select type...",
  emptyTextVariant,
  size,
  disabled,
  readOnly,
  valueMuted,
  buttonVariant = "outline",
  showChevron = true,
  shape,
  dense,
  children,
}: IssueTypeSelectorProps) {
  const { pool } = useIssueTypePool({ issueTypes });

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [pool],
      maxSelections: 1,
      searchPlaceholder: "Find type...",
      emptyMessage: "No matching Project types",
      noItemsMessage: "No Project types yet",
    }),
    [pool],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    if (!value) return [];
    const entity = pool.items.find((item) => item.id === value);
    if (!entity) return [];
    return [{ id: value, poolId: pool.id, entity }];
  }, [value, pool]);

  // Veto all internal state changes — fire onValueChange instead.
  // The display only updates when the parent changes the `value` prop.
  const handleBeforeSelect = useCallback(
    (entity: { id: string }) => {
      onValueChange?.(entity.id);
      return false;
    },
    [onValueChange],
  );

  return (
    <EntitySelector variant={variant} config={config} value={selections} onBeforeSelect={handleBeforeSelect} title="Type">
      {children ?? (
        <>
          {valueMuted || readOnly ? (
            <SelectionEntityTrigger
              toEntity={selectionToIssueTypeEntity}
              placeholder={placeholder}
              emptyTextVariant={emptyTextVariant}
              title="Type"
              size={size}
              disabled={disabled}
              readOnly={readOnly}
              showChevron={showChevron}
              buttonVariant={buttonVariant}
              shape={shape}
              dense={dense}
              valueMuted
            />
          ) : (
            <EntitySelectorTrigger
              placeholder={placeholder}
              size={toEntityTriggerSize(size)}
              disabled={disabled}
            />
          )}
          <EntitySelectorContent>
            <EntitySelectorInput />
            <IssueTypePool value={pool.id} />
            <EntitySelectorEmpty>No matching Project types</EntitySelectorEmpty>
          </EntitySelectorContent>
        </>
      )}
    </EntitySelector>
  );
}

